import React, { useState, useEffect } from 'react';
import { X, User, Mail, Shield, Calendar, CheckCircle, XCircle, Clock, Lock, Eye, EyeOff, KeyRound, ChevronDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { authService } from '../../services/auth/auth.service';

interface UserProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserProfile {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  roles: Array<{
    id: string;
    nombre: string;
    descripcion?: string;
    modulos: Array<{
      id: string;
      nombre: string;
      ruta?: string;
      permisos: Array<{
        id: string;
        nombre: string;
      }>;
    }>;
  }>;
  ultimoAcceso?: string;
  cliente?: {
    id: string;
    nombres: string;
    apellidos: string;
    telefono: string;
  } | null;
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { user } = usePermissions();

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      loadProfile();
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      // Intentar obtener el perfil del backend
      const profileData = await authService.getCurrentUser();
      setProfile(profileData as unknown as UserProfile);
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      // Fallback a los datos del store
      if (user) {
        setProfile({
          id: user.id,
          email: user.email,
          nombreCompleto: user.nombreCompleto,
          tipoUsuario: user.tipoUsuario,
          estado: user.estado,
          roles: user.roles || [],
          cliente: null
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);
    try {
      const response = await authService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (response.success) {
        setPasswordSuccess(true);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setPasswordSuccess(false);
          setShowChangePassword(false);
        }, 3000);
      } else {
        setPasswordError(response.message || 'Error al cambiar la contraseña');
      }
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Formatear fecha
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener color según tipo de usuario
  const getUserTypeColor = (tipo: string) => {
    switch (tipo) {
      case 'administrador':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cliente':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isOpen) return null;

  const displayUser = (profile || user) as UserProfile | null;

  return (
    <div className="fixed inset-0 z-40 overflow-hidden pointer-events-none">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-300 pointer-events-auto ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Drawer - Ancho reducido: max-w-md (448px) */}
      <section
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out pointer-events-auto ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 41 }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex items-center justify-between shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Mi Perfil</h2>
                <p className="text-xs text-blue-100">
                  {displayUser?.roles?.map(r => r.nombre).join(', ') || (displayUser?.tipoUsuario === 'cliente' ? 'Cliente' : 'Administrador')}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Cargando...</span>
              </div>
            ) : !displayUser ? (
              <div className="text-center py-8">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-gray-600">No se pudo cargar la información del perfil</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Avatar y Nombre */}
                <div className="text-center pb-5 border-b border-gray-100">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl font-bold">
                    {displayUser.nombreCompleto?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {displayUser.nombreCompleto}
                  </h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border mt-2 ${getUserTypeColor(displayUser.tipoUsuario)}`}>
                    {displayUser?.roles?.map(r => r.nombre).join(', ') || (displayUser.tipoUsuario === 'administrador' ? 'Administrador' : 'Cliente')}
                  </span>
                  <div className="flex items-center justify-center mt-2 space-x-1">
                    {displayUser.estado ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600">Activo</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-red-600">Inactivo</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Información de Contacto */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Información de Contacto
                  </h4>
                  
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                    <div className="flex items-start space-x-3">
                      <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm font-medium text-gray-900 break-all">
                          {displayUser.email}
                        </p>
                      </div>
                    </div>

                    {displayUser.cliente && (
                      <div className="flex items-start space-x-3">
                        <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Teléfono</p>
                          <p className="text-sm font-medium text-gray-900">
                            {displayUser.cliente.telefono}
                          </p>
                        </div>
                      </div>
                    )}

                    {displayUser.ultimoAcceso && (
                      <div className="flex items-start space-x-3">
                        <Clock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Último acceso</p>
                          <p className="text-sm text-gray-700">
                            {formatDate(displayUser.ultimoAcceso)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Roles y Permisos */}
                {displayUser.roles && displayUser.roles.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center">
                      <Shield className="w-4 h-4 mr-1" />
                      Roles y Accesos
                    </h4>
                    
                    <div className="space-y-2">
                      {displayUser.roles.map((rol, index) => (
                        <div key={rol.id || index} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                          <p className="font-medium text-blue-900 text-sm">{rol.nombre}</p>
                          {rol.descripcion && (
                            <p className="text-xs text-blue-700 mt-0.5">{rol.descripcion}</p>
                          )}
                          
                          {/* Módulos con permisos */}
                          {rol.modulos && rol.modulos.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {rol.modulos.slice(0, 3).map((modulo, mIndex) => (
                                <div key={modulo.id || mIndex} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600">• {modulo.nombre}</span>
                                  {modulo.permisos && modulo.permisos.length > 0 && (
                                    <span className="text-gray-400">
                                      {modulo.permisos.length} permiso(s)
                                    </span>
                                  )}
                                </div>
                              ))}
                              {rol.modulos.length > 3 && (
                                <p className="text-xs text-gray-400 italic">
                                  +{rol.modulos.length - 3} módulos más
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Información de Cuenta */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Información de Cuenta
                  </h4>
                  
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">ID de Usuario</span>
                      <span className="font-mono text-xs text-gray-700 bg-gray-200 px-2 py-0.5 rounded">
                        {displayUser.id?.slice(0, 8)}...
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tipo de cuenta</span>
                      <span className="text-gray-700 capitalize">
                        {displayUser.tipoUsuario}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cambiar Contraseña */}
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowChangePassword(!showChangePassword);
                      setPasswordError(null);
                      setPasswordSuccess(false);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10 rounded-lg border border-[#4CAF50]/20 hover:border-[#2196F3]/30 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <KeyRound className="w-5 h-5 text-[#4CAF50]" />
                      <span className="text-sm font-semibold text-gray-900">Cambiar Contraseña</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${showChangePassword ? 'rotate-180' : ''}`} />
                  </button>

                  {showChangePassword && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                      {passwordSuccess ? (
                        <div className="text-center py-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <p className="text-sm font-medium text-green-700">Contraseña actualizada exitosamente</p>
                        </div>
                      ) : (
                        <form onSubmit={handleChangePassword} className="space-y-4">
                          {passwordError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                              <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                              <p className="text-xs text-red-700">{passwordError}</p>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Contraseña actual</label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type={showCurrentPassword ? 'text' : 'password'}
                                required
                                value={passwordForm.currentPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                className="w-full pl-9 pr-10 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 transition-all"
                                placeholder="••••••••"
                              />
                              <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2196F3]"
                              >
                                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Nueva contraseña</label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type={showNewPassword ? 'text' : 'password'}
                                required
                                minLength={6}
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                className="w-full pl-9 pr-10 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 transition-all"
                                placeholder="Mínimo 6 caracteres"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2196F3]"
                              >
                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Confirmar nueva contraseña</label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="password"
                                required
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 transition-all"
                                placeholder="Repite la nueva contraseña"
                              />
                            </div>
                          </div>

                          <button
                            type="submit"
                            disabled={passwordLoading}
                            className={`w-full py-2.5 rounded-lg font-semibold text-white text-sm transition-all duration-300 ${
                              passwordLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:from-[#43a047] hover:to-[#1e88e5] hover:shadow-md'
                            }`}
                          >
                            {passwordLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 shrink-0">
            <button
              onClick={handleClose}
              className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserProfileDrawer;
